import { useMemo } from 'react';
import { Card, Stack, Title, Text, Badge } from '@repletoryreach/sdk-ui';
import type { templates as templatesNs } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../../lib/format';
import { WhatsAppPreview } from '@repletoryreach/sdk-ui/data-display';
import { parseComponents } from './parseComponents';
import { useT } from '../../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templatesNs.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

function highlight(text: string): React.ReactNode {
  const parts = text.split(/(\{\{\s*\d+\s*\}\})/g);
  return parts.map((p, i) =>
    /\{\{\s*\d+\s*\}\}/.test(p) ? (
      <span
        key={i}
        style={{
          background: 'rgba(245, 158, 11, 0.18)',
          color: 'var(--rr-color-primary, #0d9488)',
          padding: '0 4px',
          borderRadius: 4,
          fontFamily: 'var(--rr-font-mono, ui-monospace, monospace)',
          fontSize: '0.85em'
        }}
      >
        {p.replace(/\s+/g, '')}
      </span>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export function OverviewTab({ template }: { template: Template }) {
  const t = useT();
  const parsed = useMemo(() => parseComponents(template.components), [template.components]);
  const header = parsed.find((c) => c.type === 'HEADER');
  const body = parsed.find((c) => c.type === 'BODY');
  const footer = parsed.find((c) => c.type === 'FOOTER');
  const buttonsBlock = parsed.find((c) => c.type === 'BUTTONS');

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('templates.view.overview.preview')}</Title>
          <Text fz="xs" c={MUTED}>
            {t('templates.view.overview.previewHint')}
          </Text>
          {/* Promoted into sdk-ui: this was inline-styled with dark-only
              colour fallbacks and duplicated in admin-ui. */}
          {/* With the handset around it: a bubble on a white card reads as a
              quote from a document, and the question this card answers is
              "what will the customer actually receive?". */}
          <WhatsAppPreview
            chrome
            businessName={t('templates.view.overview.businessName')}
            timestamp={t('templates.view.overview.sampleTime')}
            composerLabel={t('templates.view.overview.composer')}
            header={header ? { text: header.text, format: header.format, mediaUrl: header.example?.header_handle?.[0] } : null}
            body={body?.text ?? null}
            footer={footer?.text ?? null}
            buttons={buttonsBlock?.buttons ?? null}
            emptyBodyLabel={t('templates.view.overview.emptyBody')}
            renderPlaceholder={(position) => (
              <Badge size="xs" color="orange" variant="light">
                <span aria-hidden>{position}</span>
                {/* The chip read as a bare "1" to a screen reader, with no
                    indication it is a placeholder. */}
                <span className="rr-visually-hidden">
                  {t('templates.view.overview.placeholderLabel', { position })}
                </span>
              </Badge>
            )}
          />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('templates.view.overview.metadata')}</Title>
          <Row label={t('templates.columns.code')} value={<code>{template.code}</code>} />
          <Row label={t('templates.columns.category')} value={<Badge size="xs" variant="light">{template.category}</Badge>} />
          <Row label={t('common.language')} value={<Badge size="xs" variant="light">{template.language}</Badge>} />
          <Row label={t('common.created')} value={`${formatDate(template.created_at)} (${formatRelative(template.created_at)})`} />
          <Row label={t('common.updated')} value={`${formatDate(template.updated_at)} (${formatRelative(template.updated_at)})`} />
          {/* §4 item 5: both belong on the card literally named "Meta sync"
              and neither was shown, so it could not answer "has Meta actually
              seen this?". */}
          <Row
            label={t('templates.view.overview.metaStatus')}
            value={template.meta_status
              ? <Badge size="xs" variant="light">{template.meta_status}</Badge>
              : <span style={{ color: MUTED }}>{t('templates.view.overview.metaNeverSeen')}</span>}
          />
          <Row
            label={t('templates.view.overview.importedAt')}
            value={template.imported_at
              ? `${formatDate(template.imported_at)} (${formatRelative(template.imported_at)})`
              : <span style={{ color: MUTED }}>{t('common.none')}</span>}
          />
          {/* §4 item 2: an auto-approved template rendered a green "approved"
              badge indistinguishable from the real thing, on the one page
              whose job is explaining approval state. */}
          {template.dev_fallback && (
            <Row
              label={t('templates.devFallback')}
              value={<Badge size="xs" color="orange" variant="light">{t('templates.devFallback.tooltip')}</Badge>}
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}
